import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Clock, Lock, ListChecks } from "lucide-react";

const BG_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_mls-deadline-master/artifacts/gwkjr2km_home2.jpg";
const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_mls-deadline-master/artifacts/jbrp9xqq_equitasfit%20%28Logo%29%20%284%29.png";

export default function Login() {
  useDocumentTitle();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      nav("/app");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-10 px-6 lg:px-12 py-10 lg:py-16">
        {/* Left: editorial hero */}
        <section className="lg:col-span-7 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Equitas 1%" className="h-11 w-11 rounded-full object-cover shadow-sm" />
            <div className="flex flex-col leading-none">
              <span className="overline text-stone-500">Equitas Homes</span>
              <span className="text-base font-heading font-semibold tracking-tight text-stone-900 mt-1">
                Listing Workflow
              </span>
            </div>
          </div>

          <div className="mt-14 lg:mt-24 max-w-2xl">
            <p className="overline text-stone-500 mb-6">Agent Workflow</p>
            <h1 className="font-heading font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900">
              Every Home We're Guiding To <span className="font-serif-italic text-brand">The Closing Table</span>
            </h1>
            <p className="mt-6 text-stone-600 text-base leading-relaxed max-w-xl">
              A guided workflow for every home you take. Seven phases,
              deadline-aware, so nothing slips between listing appointment
              and closing table.
            </p>

            <div className="mt-8 flex flex-wrap gap-2">
              <span className="chip">
                <Clock className="h-3.5 w-3.5" />
                About 5 minutes to set up
              </span>
              <span className="chip">
                <Lock className="h-3.5 w-3.5" />
                Private &amp; confidential
              </span>
              <span className="chip">
                <ListChecks className="h-3.5 w-3.5" />
                7 short phases
              </span>
            </div>
          </div>

          {/* Realtor quote card, similar to Equitas Louis Fong card */}
          <div className="mt-14 max-w-md">
            <div className="rounded-md border border-stone-200 bg-white p-5 flex items-start gap-4">
              <img
                src={LOGO_URL}
                alt="Louis Fong"
                className="h-14 w-14 rounded-full object-cover shrink-0 shadow-sm"
              />
              <div>
                <p className="text-sm font-medium text-stone-900">Louis Fong</p>
                <p className="text-xs text-stone-500 mt-0.5">Realtor · Equitas Homes</p>
                <p className="font-serif-italic text-stone-700 text-sm leading-relaxed mt-2.5">
                  “A repeatable playbook is the difference between a sale and a great sale.”
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Right: form + image */}
        <section className="lg:col-span-5 flex flex-col gap-8">
          <div className="relative rounded-md overflow-hidden aspect-[4/3] hidden lg:block">
            <img src={BG_URL} alt="Elegant living room" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute bottom-4 left-4">
              <span className="chip chip-dark">Interior · Craftsman</span>
            </div>
          </div>

          <div className="rounded-md border border-stone-200 bg-white p-7">
            <p className="overline text-stone-500 mb-3">Log in</p>
            <h2 className="text-2xl font-heading font-semibold tracking-tight text-stone-900">
              Welcome back
            </h2>
            <p className="text-sm text-stone-500 mt-1.5 mb-6">
              Continue tracking your homes.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-stone-700">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@brokerage.com"
                  required
                  className="h-11 rounded-md border-stone-300 bg-[#faf7f2]/40"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-stone-700">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-11 rounded-md border-stone-300 bg-[#faf7f2]/40"
                />
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={busy}
                className="w-full h-11 text-white rounded-full"
                style={{ backgroundColor: "#010082" }}
              >
                {busy ? "Signing in..." : "Sign in"}
              </Button>
            </form>

            <p className="mt-6 text-sm text-stone-600 text-center">
              No account yet?{" "}
              <Link
                to="/signup"
                data-testid="go-to-signup-link"
                className="font-medium underline underline-offset-4"
                style={{ color: "#010082" }}
              >
                Create one
              </Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
